import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BethelAppClasses, bethelQuery } from '../../api/config';
import { Banner, Button, Card, EmptyState, cx } from '../ui';

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'unanswered', label: 'Unanswered' },
    { key: 'answered', label: 'Answered' },
    { key: 'private', label: 'Private' },
];

const PrayerRequestsManager = () => {
    const [prayerRequests, setPrayerRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchPrayerRequests();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchPrayerRequests = async () => {
        try {
            setLoading(true);
            setError('');

            // Direct fetch to the app server — no global SDK switch. See
            // bethelQuery in api/config.js for why.
            const where = {};
            if (filter === 'unanswered') {
                where.prayed = { $ne: true };
            } else if (filter === 'answered') {
                where.prayed = true;
            } else if (filter === 'private') {
                where.private = true;
            }

            const requests = await bethelQuery(BethelAppClasses.PrayerRequest, {
                where,
                include: 'user',
                order: '-createdAt',
                limit: 500,
            });
            setPrayerRequests(requests);
        } catch (err) {
            console.error('Error fetching prayer requests:', err);
            setError('We could not load the prayer requests. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const markAsAnswered = async (requestId) => {
        try {
            // The forms server verifies we're a pastor and writes it to the app.
            const result = await Parse.Cloud.run('pastorsMarkPrayerAnswered', { prayerRequestId: requestId });

            if (result.success) {
                setPrayerRequests((prev) =>
                    prev.map((req) => {
                        if (req.id === requestId) req.set('prayed', true);
                        return req;
                    })
                );
            }
        } catch (err) {
            console.error('Error marking prayer as answered:', err);
            setError(err.message || 'We could not mark the prayer as answered. Please try again.');
        }
    };

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const filteredRequests = prayerRequests.filter((request) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        const name = request.get('name')?.toLowerCase() || '';
        const prayerText = request.get('prayerRequest')?.toLowerCase() || '';
        const email = request.get('email')?.toLowerCase() || '';
        return name.includes(q) || prayerText.includes(q) || email.includes(q);
    });

    const stats = [
        { label: 'Total', value: prayerRequests.length, tone: 'text-neutral-900' },
        { label: 'Unanswered', value: prayerRequests.filter((r) => !r.get('prayed')).length, tone: 'text-amber-700' },
        { label: 'Answered', value: prayerRequests.filter((r) => r.get('prayed')).length, tone: 'text-emerald-700' },
        { label: 'Private', value: prayerRequests.filter((r) => r.get('private')).length, tone: 'text-brand-800' },
    ];

    return (
        <div className="space-y-4">
            {error && <Banner tone="danger">{error}</Banner>}

            {/* Toolbar */}
            <Card>
                <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative lg:w-80">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search name, email, or request"
                            className="!pl-9"
                            aria-label="Search prayer requests"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {FILTERS.map((f) => (
                            <Button
                                key={f.key}
                                size="sm"
                                variant={filter === f.key ? 'primary' : 'secondary'}
                                onClick={() => setFilter(f.key)}
                            >
                                {f.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {stats.map((s) => (
                    <Card key={s.label} className="px-5 py-4">
                        <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">{s.label}</p>
                        <p className={cx('mt-1 font-display text-2xl font-semibold tabular-nums', s.tone)}>{s.value}</p>
                    </Card>
                ))}
            </div>

            {/* List */}
            {loading ? (
                <Card>
                    <EmptyState title="Loading prayer requests…" />
                </Card>
            ) : filteredRequests.length === 0 ? (
                <Card>
                    <EmptyState
                        title="No prayer requests"
                        description={searchTerm ? 'Nothing matches your search.' : 'Nothing in this filter.'}
                    />
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredRequests.map((request) => {
                        const answered = request.get('prayed');
                        return (
                            <Card
                                key={request.id}
                                className={cx('px-5 py-4', answered && 'border-l-4 border-l-emerald-500')}
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="font-display text-base font-semibold text-neutral-900">
                                                {request.get('name') || 'Anonymous'}
                                            </h3>
                                            {request.get('private') && (
                                                <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xxs font-medium text-brand-800 ring-1 ring-inset ring-brand-200">
                                                    Private
                                                </span>
                                            )}
                                            {answered && (
                                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xxs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                                    Answered
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-500">
                                            <span>{formatDate(request.get('createdAt'))}</span>
                                            {request.get('email') && (
                                                <a href={`mailto:${request.get('email')}`} className="text-brand-700 hover:underline">
                                                    {request.get('email')}
                                                </a>
                                            )}
                                            {request.get('phoneNumber') && <span>{request.get('phoneNumber')}</span>}
                                        </div>
                                    </div>
                                    {!answered && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            className="flex-shrink-0"
                                            onClick={() => markAsAnswered(request.id)}
                                        >
                                            Mark answered
                                        </Button>
                                    )}
                                </div>

                                <p className="mt-3 whitespace-pre-wrap text-sm text-neutral-700">
                                    {request.get('prayerRequest')}
                                </p>

                                {request.get('prayerCount') > 0 && (
                                    <p className="mt-3 text-xs text-neutral-400">
                                        🙏 {request.get('prayerCount')} {request.get('prayerCount') === 1 ? 'person has' : 'people have'} prayed for this
                                    </p>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PrayerRequestsManager;
